import { isBreadcrumbLinkDisabledByRule, useBreadcrumbItemName } from "@/common/helpers/breadcrumb";
import { cn } from "@/common/helpers/utils";
import { useNavigationLookups } from "@/common/hooks/use-navigation";
import { breadcrumbDisableWhenParentUuid } from "@/config/navigation/breadcrumb.config";
import { ChevronRight, ServerCrashIcon } from "lucide-react";
import * as React from "react";
import { Link } from "wouter";
import { Loader, LoaderSize, LoaderVariant } from "./loader";

const Breadcrumb = ({
  ref,
  ...props
}: React.ComponentProps<"nav"> & {
  separator?: React.ReactNode;
}) => <nav ref={ref} aria-label="breadcrumb" {...props} />;
Breadcrumb.displayName = "Breadcrumb";

const BreadcrumbList = ({ className, ref, ...props }: React.ComponentProps<"ol">) => (
  <ol
    ref={ref}
    className={cn(
      "text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm wrap-break-word sm:gap-2.5",
      className,
    )}
    {...props}
  />
);
BreadcrumbList.displayName = "BreadcrumbList";

interface BreadcrumbItemProps extends React.ComponentPropsWithRef<"li"> {
  value: string;
  previousValues: string[];
}

const BreadcrumbItem = ({ className, value, previousValues, ref, ...props }: BreadcrumbItemProps) => {
  const { isLoading, error, data } = useBreadcrumbItemName(value, previousValues);
  const { pathMap, disabledSegments } = useNavigationLookups();
  const href = (previousValues.length > 0 ? `/${previousValues.join("/")}` : "") + `/${value}`;
  const routeItem = pathMap.get(href);
  const parentHref = previousValues.length > 0 ? `/${previousValues.join("/")}` : undefined;
  const parentRouteItem = parentHref ? pathMap.get(parentHref) : undefined;
  const isDisabled = isBreadcrumbLinkDisabledByRule({
    value,
    previousValues,
    routeItem,
    parentRouteItem,
    disabledSegments,
    rules: breadcrumbDisableWhenParentUuid,
  });
  return (
    <li ref={ref} className={cn("inline-flex items-center gap-1.5", className)} {...props}>
      <BreadcrumbLink href={href} isDisabled={isDisabled}>
        {isLoading ? (
          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
        ) : error ? (
          <ServerCrashIcon />
        ) : (
          data
        )}
      </BreadcrumbLink>
    </li>
  );
};
BreadcrumbItem.displayName = "BreadcrumbItem";

type BreadcrumbLinkProps = React.ComponentProps<"a"> & {
  isDisabled?: boolean;
};

const BreadcrumbLink = ({ children, className, href, isDisabled, onClick, ref, ...props }: BreadcrumbLinkProps) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  const linkClassName = cn(
    "transition-colors",
    isDisabled ? "cursor-default text-muted-foreground" : "hover:text-foreground cursor-pointer",
    className,
  );
  return (
    <Link
      ref={ref}
      to={href ?? ""}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : undefined}
      onClick={handleClick}
      {...props}
    >
      <div className={linkClassName}>{children}</div>
    </Link>
  );
};
BreadcrumbLink.displayName = "BreadcrumbLink";

const BreadcrumbSeparator = ({ children, className, ...props }: React.ComponentProps<"li">) => (
  <li role="presentation" aria-hidden="true" className={cn("[&>svg]:h-3.5 [&>svg]:w-3.5", className)} {...props}>
    {children ?? <ChevronRight />}
  </li>
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

export { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator };
